import { useState, useMemo } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { useDebounce } from "@/MyCode/hooks/useDebounce"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"
import { PieChart } from "@/components/mortgage/PieChart"
import { Button } from "@/components/ui/button"
import { Save } from "lucide-react"

export function GeneralAPRCalculator() {
    // Form state
    const [loanAmount, setLoanAmount] = useState(100000)
    const [loanTermYears, setLoanTermYears] = useState(10)
    const [loanTermMonths, setLoanTermMonths] = useState(0)
    const [interestRate, setInterestRate] = useState(6)
    const [compoundFrequency, setCompoundFrequency] = useState("Monthly")
    const [payBackFrequency, setPayBackFrequency] = useState("Every Month")
    const [loanedFees, setLoanedFees] = useState(100)
    const [upfrontFees, setUpfrontFees] = useState(2500)

    // Debounced values
    const debouncedLoanAmount = useDebounce(loanAmount, 500)
    const debouncedLoanTermYears = useDebounce(loanTermYears, 500)
    const debouncedLoanTermMonths = useDebounce(loanTermMonths, 500)
    const debouncedInterestRate = useDebounce(interestRate, 500)
    const debouncedCompoundFrequency = useDebounce(compoundFrequency, 500)
    const debouncedPayBackFrequency = useDebounce(payBackFrequency, 500)
    const debouncedLoanedFees = useDebounce(loanedFees, 500)
    const debouncedUpfrontFees = useDebounce(upfrontFees, 500)

    // Calculate total loan term in months
    const totalMonths = useMemo(() => {
        return debouncedLoanTermYears * 12 + debouncedLoanTermMonths
    }, [debouncedLoanTermYears, debouncedLoanTermMonths])

    // Calculate amount financed (loan amount + loaned fees)
    const amountFinanced = useMemo(() => {
        return debouncedLoanAmount + debouncedLoanedFees
    }, [debouncedLoanAmount, debouncedLoanedFees])

    // Calculate monthly payment
    const monthlyPayment = useMemo(() => {
        if (amountFinanced <= 0 || totalMonths <= 0) return 0
        const monthlyRate = debouncedInterestRate / 100 / 12

        if (monthlyRate === 0) {
            return amountFinanced / totalMonths
        }

        return (amountFinanced * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
            (Math.pow(1 + monthlyRate, totalMonths) - 1)
    }, [amountFinanced, totalMonths, debouncedInterestRate])

    // Calculate total payments
    const totalPayments = useMemo(() => {
        return monthlyPayment * totalMonths
    }, [monthlyPayment, totalMonths])

    // Calculate total interest
    const totalInterest = useMemo(() => {
        return totalPayments - amountFinanced
    }, [totalPayments, amountFinanced])

    // Calculate all payments and fees
    const allPaymentsAndFees = useMemo(() => {
        return totalPayments + debouncedUpfrontFees
    }, [totalPayments, debouncedUpfrontFees])

    // Calculate APR using iterative method
    // APR accounts for upfront fees - it's the rate where PV of payments equals (loan amount - upfront fees)
    const realAPR = useMemo(() => {
        if (amountFinanced <= 0 || totalMonths <= 0 || monthlyPayment <= 0) return debouncedInterestRate

        // Effective loan amount (what borrower actually receives after upfront fees)
        // Note: loaned fees are already included in amountFinanced, but upfront fees are paid separately
        const effectiveLoanAmount = debouncedLoanAmount - debouncedUpfrontFees

        if (effectiveLoanAmount <= 0) return debouncedInterestRate

        // Use binary search to find APR
        // APR is the rate where PV of all payments equals effective loan amount
        let low = 0
        let high = 0.5 // 50% APR max
        let apr = debouncedInterestRate / 100
        const tolerance = 0.0001

        for (let i = 0; i < 100; i++) {
            apr = (low + high) / 2
            const testMonthlyRate = apr / 12

            // Calculate present value of payments at this APR
            const pv = testMonthlyRate === 0
                ? monthlyPayment * totalMonths
                : (monthlyPayment * (1 - Math.pow(1 + testMonthlyRate, -totalMonths))) / testMonthlyRate

            if (Math.abs(pv - effectiveLoanAmount) < tolerance) {
                break
            }

            if (pv < effectiveLoanAmount) {
                high = apr
            } else {
                low = apr
            }
        }

        return apr * 100
    }, [debouncedLoanAmount, debouncedUpfrontFees, monthlyPayment, totalMonths, amountFinanced, debouncedInterestRate])

    // Calculate pie chart data
    const pieChartData = useMemo(() => {
        const principal = debouncedLoanAmount
        const interest = totalInterest
        const fees = debouncedLoanedFees + debouncedUpfrontFees

        return [
            {
                label: "Principal",
                value: Math.max(0, principal),
                color: "#3b82f6", // blue
            },
            {
                label: "Interest",
                value: Math.max(0, interest),
                color: "#ef4444", // red
            },
            {
                label: "Fees",
                value: Math.max(0, fees),
                color: "#f59e0b", // amber
            },
        ]
    }, [debouncedLoanAmount, totalInterest, debouncedLoanedFees, debouncedUpfrontFees])

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.loanAmount !== undefined) setLoanAmount(Number(data.loanAmount))
        if (data.loanTermYears !== undefined) setLoanTermYears(Number(data.loanTermYears))
        if (data.loanTermMonths !== undefined) setLoanTermMonths(Number(data.loanTermMonths))
        if (data.interestRate !== undefined) setInterestRate(Number(data.interestRate))
        if (data.compoundFrequency !== undefined) setCompoundFrequency(data.compoundFrequency)
        if (data.payBackFrequency !== undefined) setPayBackFrequency(data.payBackFrequency)
        if (data.loanedFees !== undefined) setLoanedFees(Number(data.loanedFees))
        if (data.upfrontFees !== undefined) setUpfrontFees(Number(data.upfrontFees))
    }

    const steps: Step[] = [
        {
            id: "loan-details",
            title: "Loan Details",
            description: "Enter your loan information",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanAmount",
                    label: "Loan Amount",
                    placeholder: "100000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanAmount: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanTermYears",
                    label: "Loan Term (years)",
                    placeholder: "10",
                    required: true,
                    minValue: 0,
                    maxValue: 50,
                    allowNegative: false,
                    incrementValues: [1, 5],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanTermYears: 'required|integer|min:0|max:50'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanTermMonths",
                    label: "Loan Term (months)",
                    placeholder: "0",
                    required: true,
                    minValue: 0,
                    maxValue: 11,
                    allowNegative: false,
                    incrementValues: [1, 3],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanTermMonths: 'required|integer|min:0|max:11'
                    }
                },
                {
                    type: "number",
                    name: "interestRate",
                    label: "Interest Rate (%)",
                    placeholder: "6",
                    required: true,
                    minValue: 0,
                    maxValue: 100,
                    step: 0.01,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        interestRate: 'required|numeric|min:0|max:100'
                    }
                },
                // {
                //     type: "select",
                //     name: "compoundFrequency",
                //     label: "Compound",
                //     placeholder: "Select compound frequency",
                //     required: true,
                //     options: {
                //         items: [
                //             { label: "Monthly", value: "Monthly" },
                //             { label: "Quarterly", value: "Quarterly" },
                //             { label: "Semi-Annually", value: "Semi-Annually" },
                //             { label: "Annually", value: "Annually" },
                //         ]
                //     },
                //     ui: { colSpan: 6 },
                //     laravelValidationRules: {
                //         compoundFrequency: 'required'
                //     }
                // },
                // {
                //     type: "select",
                //     name: "payBackFrequency",
                //     label: "Pay Back",
                //     placeholder: "Select pay back frequency",
                //     required: true,
                //     options: {
                //         items: [
                //             { label: "Every Month", value: "Every Month" },
                //             { label: "Every Quarter", value: "Every Quarter" },
                //             { label: "Every 6 Months", value: "Every 6 Months" },
                //             { label: "Annually", value: "Annually" },
                //         ]
                //     },
                //     ui: { colSpan: 6 },
                //     laravelValidationRules: {
                //         payBackFrequency: 'required'
                //     }
                // },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanedFees",
                    label: "Loaned Fees",
                    placeholder: "100",
                    required: false,
                    description: "Fees added to the loan amount",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanedFees: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "upfrontFees",
                    label: "Upfront Fees",
                    placeholder: "2500",
                    required: false,
                    description: "Fees paid upfront out of pocket",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        upfrontFees: 'nullable|numeric|min:0'
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">General APR Calculator</h1>
                <p className="text-muted-foreground">
                    Calculate the Annual Percentage Rate (APR) for your loan including all fees
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            loanAmount: loanAmount,
                            loanTermYears: loanTermYears,
                            loanTermMonths: loanTermMonths,
                            interestRate: interestRate,
                            compoundFrequency: compoundFrequency,
                            payBackFrequency: payBackFrequency,
                            loanedFees: loanedFees,
                            upfrontFees: upfrontFees,
                        }}
                        resetOnInitialValuesChange={false}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-6"
                >
                    {/* Pie Chart */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.3 }}
                        className="bg-card border border-border rounded-lg p-6"
                    >
                        <h2 className="text-xl font-semibold mb-4">Payment Breakdown</h2>
                        <div className="flex justify-center">
                            <PieChart data={pieChartData} />
                        </div>
                    </motion.div>

                    {/* Results Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.3 }}
                        className="bg-card border-2 border-primary/20 rounded-lg p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">Results</h2>
                            <Button variant="outline" size="sm">
                                <Save className="h-4 w-4 mr-2" />
                                Save this calculation
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {/* Real APR */}
                            <div className="pb-4 border-b border-border">
                                <p className="text-sm text-muted-foreground mb-1">Real APR</p>
                                <p className="text-3xl font-bold text-primary">
                                    <AnimatedNumber value={realAPR} decimals={3} />%
                                </p>
                            </div>

                            {/* Amount Financed */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Amount Financed</span>
                                <span className="text-base font-semibold font-mono tabular-nums">
                                    $<AnimatedNumber value={amountFinanced} decimals={2} />
                                </span>
                            </div>

                            {/* Upfront Out-of-Pocket Fees */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Upfront Out-of-Pocket Fees</span>
                                <span className="text-base font-semibold font-mono tabular-nums">
                                    $<AnimatedNumber value={debouncedUpfrontFees} decimals={2} />
                                </span>
                            </div>

                            {/* Payment Every Month */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Payment Every Month</span>
                                <span className="text-base font-semibold font-mono tabular-nums">
                                    $<AnimatedNumber value={monthlyPayment} decimals={2} />
                                </span>
                            </div>

                            {/* Total of Payments */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Total of {totalMonths} Payments</span>
                                <span className="text-base font-semibold font-mono tabular-nums">
                                    $<AnimatedNumber value={totalPayments} decimals={2} />
                                </span>
                            </div>

                            {/* Total Interest */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Total Interest</span>
                                <span className="text-base font-semibold font-mono tabular-nums">
                                    $<AnimatedNumber value={totalInterest} decimals={2} />
                                </span>
                            </div>

                            {/* All Payments and Fees */}
                            <div className="pt-4 border-t border-border">
                                <div className="flex justify-between items-center">
                                    <span className="text-base font-semibold">All Payments and Fees</span>
                                    <span className="text-lg font-bold font-mono tabular-nums">
                                        $<AnimatedNumber value={allPaymentsAndFees} decimals={2} />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    )
}

